import { NoteViewer } from "@/components/note/viewer/NoteViewer";
import { SpringAnimator } from "@/mds";
import { HoveringState } from "@/design-system/components/item-list/rows/item-preview/HoveringState";
import { ZIndex } from "@/domains/design/constants";
import { css, keyframes } from "@/domains/emotion";
import { INoteObservable } from "@/store/note/types";
import { useAppStore } from "@/store/utils/hooks";
import { MDSNotePreviewContainer } from "@/mds/components/note-preview-container";
import {
  FloatingNode,
  FloatingPortal,
  Placement,
  flip,
  offset,
  safePolygon,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { AnimatePresence } from "motion/react";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { mdsSpacings } from "@/design-system/foundations/typography";

export const HEADS_UP_PREVIEW_PLACEMENT: Placement = "left-end";

export interface FloatingNotePreviewCardProps {
  state: HoveringState;
  noteObservable?: INoteObservable;
  placement?: Placement;
  highlightText?: string;
}

export const FloatingNotePreview = observer<FloatingNotePreviewCardProps>(
  ({ noteObservable, state, placement, highlightText }) => {
    const { store } = useAppStore();
    const nodeId = useFloatingNodeId();

    const isOpen = Boolean(noteObservable && state.hovering);

    const resolvedPlacement = placement ?? "bottom-end";
    const isLeftPlacement = resolvedPlacement.startsWith("left");

    const middleware = useMemo(
      () => [
        offset({ mainAxis: 18 }),
        isLeftPlacement
          ? flip({ fallbackPlacements: ["left-start"], fallbackStrategy: "initialPlacement" })
          : flip(),
      ],
      [isLeftPlacement]
    );

    const { x, y, refs, strategy, context } = useFloating({
      nodeId,
      strategy: "fixed",
      middleware,
      open: isOpen,
      onOpenChange: value => {
        if (value) {
          /** We don't want to open on hover states. */
          return;
        }
      },
      placement: resolvedPlacement,
    });

    useEffect(() => {
      if (state.hoveredRect) {
        refs.setPositionReference({
          getBoundingClientRect() {
            return state.hoveredRect!;
          },
        });
      }
    }, [refs, state.hoveredRect]);

    const { getReferenceProps, getFloatingProps } = useInteractions([
      useHover(context, {
        move: false,
        handleClose: safePolygon(),
        delay: {
          close: 100,
        },
      }),
      useDismiss(context),
    ]);

    const portalContent = useMemo(() => {
      if (!isOpen || !noteObservable) {
        return null;
      }

      return (
        <SpringAnimator
          key={`floating-note-${nodeId}`}
          className={animationStyles}
          onClick={e => e.stopPropagation()}
          ref={refs.setFloating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            zIndex: ZIndex.FloatingNotePreview,
          }}
          {...getFloatingProps()}
          onMouseLeave={state.handleMouseLeave}
          onMouseEnter={state.handleMouseEnter}
        >
          <MDSNotePreviewContainer
            title={noteObservable.title}
            createdAt={noteObservable.locallyCreatedAt}
            modifiedAt={noteObservable.modifiedAt}
            authorNames={noteObservable.authors.map(author => author.profileDisplayName)}
            updateOnly={true}
          >
            <NoteViewer
              className={editorStyles}
              noteObservable={noteObservable}
              goToMention={store.navigation.goToMention}
              showMetadata={false}
              highlightText={highlightText}
              disableGlobalListeners={true}
            />
          </MDSNotePreviewContainer>
        </SpringAnimator>
      );
    }, [
      getFloatingProps,
      highlightText,
      isOpen,
      nodeId,
      noteObservable,
      refs.setFloating,
      state.handleMouseEnter,
      state.handleMouseLeave,
      store,
      strategy,
      x,
      y,
    ]);

    // Don't render anything if we don't have a note to show
    if (!noteObservable) {
      return null;
    }

    return (
      <FloatingNode id={nodeId}>
        <div ref={refs.setReference} {...getReferenceProps()}></div>
        {isOpen && (
          <FloatingPortal id={`floating-note-preview-${nodeId}`}>
            <AnimatePresence mode="wait" initial={false}>
              {portalContent}
            </AnimatePresence>
          </FloatingPortal>
        )}
      </FloatingNode>
    );
  }
);

const growAndFadeAnimation = keyframes({
  from: {
    opacity: 0,
    transform: "scale(0.97)",
  },
  to: {
    opacity: 1,
    transform: "scale(1)",
  },
});

const animationStyles = css({
  width: "400px",
  animation: `${growAndFadeAnimation} .2s ease-out`,
});

const editorStyles = css({
  height: "250px", // Fixed height for the editor area
  overflowY: "auto",
  scrollbarWidth: "none",
  padding: `${mdsSpacings().md} ${mdsSpacings().md} 0 ${mdsSpacings().md}`,
  "::-webkit-scrollbar": {
    display: "none",
  },
});
